<template>
  <div class="message-wrapper" :class="{ 'sent': isSent, 'received': !isSent }">
    <div class="message-bubble" :class="{ 'sent': isSent, 'received': !isSent, 'ai-message': isAiMessage, 'deleted': isDeleted, 'has-media': hasMedia, 'failed': isFailed }">
      <div v-if="isAiMessage" class="ai-indicator">
        <i class="bi bi-cpu"></i>
        <span>IA</span>
      </div>
      
      <div v-if="userName" class="message-sender">
        {{ userName }}
      </div>
      
      <div v-if="isDeleted" class="message-deleted">
        <i class="bi bi-trash"></i>
        <span>Esta mensagem foi apagada</span>
      </div>
      
      <template v-else>
        <div v-if="isImage" class="message-media">
          <img :src="mediaUrl" :alt="content" @click="openMediaViewer" />
        </div>
        
        <div v-else-if="isVideo" class="message-media">
          <video :src="mediaUrl" controls @click="openMediaViewer"></video>
        </div>
        
        <div v-else-if="isAudio" class="message-audio">
          <audio :src="mediaUrl" controls></audio>
        </div>
        
        <div v-else-if="isDocument" class="message-document">
          <i class="bi bi-file-text"></i>
          <span>{{ content || 'Documento' }}</span>
          <a v-if="mediaUrl" :href="mediaUrl" target="_blank" class="download-link">
            <i class="bi bi-download"></i>
          </a>
        </div>
        
        <div v-else-if="isAppointmentCard" class="appointment-card">
          <div class="appointment-title">
            Consulta agendada
          </div>
          <div class="appointment-row">
            <span class="label">Paciente:</span>
            <span class="value">{{ appointment.patient || '-' }}</span>
          </div>
          <div class="appointment-row">
            <span class="label">Data:</span>
            <span class="value">{{ appointment.date || '-' }}</span>
          </div>
          <div class="appointment-row">
            <span class="label">Horário:</span>
            <span class="value">{{ appointment.time || '-' }}</span>
          </div>
          <div class="appointment-row" v-if="appointment.professional">
            <span class="label">Profissional:</span>
            <span class="value">{{ appointment.professional }}</span>
          </div>
          <div class="appointment-row" v-if="appointment.specialty">
            <span class="label">Especialidade:</span>
            <span class="value">{{ appointment.specialty }}</span>
          </div>
          <div class="appointment-row">
            <span class="label">Forma de pagamento:</span>
            <span class="value">{{ appointment.paymentMethod || '-' }}</span>
          </div>
          <div class="appointment-row" v-if="appointment.insurance">
            <span class="label">Convênio:</span>
            <span class="value">{{ appointment.insurance }}</span>
          </div>
          <div class="appointment-row" v-if="appointment.status">
            <span class="label">Status:</span>
            <span class="value">{{ appointment.status }}</span>
          </div>
        </div>
        
        <div v-else class="message-text">{{ content }}</div>
      </template>
      
      <div v-if="isFailed && isSent" class="message-failed-indicator">
        <i class="bi bi-exclamation-circle"></i>
        <span>Falha no envio</span>
      </div>
      
      <div class="message-footer">
        <div class="message-time">{{ formatTime(createdAt) }}</div>
        <div class="message-actions">
          <button v-if="!isDeleted && !isSent" @click.stop="handleReply" class="action-btn" title="Responder">
            <i class="bi bi-reply"></i>
          </button>
          <button @click.stop="showDetails = true" class="action-btn" title="Detalhes">
            <i class="bi bi-info-circle"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDetails" class="message-details-modal" @click.self="showDetails = false">
      <div class="details-content">
        <div class="details-header">
          <h5>Detalhes da Mensagem</h5>
          <button @click="showDetails = false" class="close-btn">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="details-body">
          <div class="detail-item">
            <span class="detail-label">Criada em:</span>
            <span class="detail-value">{{ formatDateTime(createdAt) }}</span>
          </div>
          <div v-if="message.sent_at" class="detail-item">
            <span class="detail-label">Enviada em:</span>
            <span class="detail-value">{{ formatDateTime(message.sent_at) }}</span>
          </div>
          <div v-if="message.delivered_at" class="detail-item">
            <span class="detail-label">Entregue em:</span>
            <span class="detail-value">{{ formatDateTime(message.delivered_at) }}</span>
          </div>
          <div v-if="message.read_at" class="detail-item">
            <span class="detail-label">Lida em:</span>
            <span class="detail-value">{{ formatDateTime(message.read_at) }}</span>
          </div>
          <div v-if="message.failed_at" class="detail-item error">
            <span class="detail-label">Falhou em:</span>
            <span class="detail-value">{{ formatDateTime(message.failed_at) }}</span>
          </div>
          <div v-if="message.deleted_at" class="detail-item">
            <span class="detail-label">Deletada em:</span>
            <span class="detail-value">{{ formatDateTime(message.deleted_at) }}</span>
          </div>
          <div v-if="whatsappMessageId" class="detail-item">
            <span class="detail-label">ID WhatsApp:</span>
            <span class="detail-value small">{{ whatsappMessageId }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import whatsappHelper from "@/helpers/whatsapp";

export default {
  name: 'MessageBubble',
  props: {
    message: {
      type: Object,
      required: true,
    },
    isSent: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    content() {
      return this.message.content || '';
    },
    createdAt() {
      return this.message.created_at;
    },
    userName() {
      return this.message.user_name || null;
    },
    userPhoto() {
      return this.message.user_photo || null;
    },
    additionalData() {
      return this.message.additional_data || {};
    },
    mediaType() {
      return this.additionalData.type || 'text';
    },
    mediaId() {
      return this.additionalData.media_id;
    },
    mediaUrl() {
      if (this.additionalData.url) {
        return this.additionalData.url;
      }
      return null;
    },
    isImage() {
      return whatsappHelper.isImage(this.mediaType);
    },
    isVideo() {
      return whatsappHelper.isVideo(this.mediaType);
    },
    isAudio() {
      return whatsappHelper.isAudio(this.mediaType);
    },
    isDocument() {
      return whatsappHelper.isDocument(this.mediaType);
    },
    isAiMessage() {
      return this.isSent && this.message.role === 'assistant' && !this.userName;
    },
    isDeleted() {
      return !!this.message.deleted_at;
    },
    isFailed() {
      return !!this.message.failed_at;
    },
    hasMedia() {
      return this.isImage || this.isVideo;
    },
    whatsappMessageId() {
      return this.additionalData.message_id || null;
    },
    isAppointmentCard() {
      return this.additionalData.type === 'appointment_card';
    },
    appointment() {
      return {
        date: this.additionalData.date || null,
        time: this.additionalData.time || null,
        professional: this.additionalData.professional || null,
        specialty: this.additionalData.specialty || null,
        paymentMethod: this.additionalData.payment_method || null,
        insurance: this.additionalData.insurance || null,
        patient: this.additionalData.patient || null,
        status: this.additionalData.status || null,
      };
    },
  },
  data() {
    return {
      showDetails: false,
    };
  },
  methods: {
    formatTime(date) {
      return whatsappHelper.formatMessageTime(date);
    },
    formatDateTime(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },
    openMediaViewer() {
      if (this.mediaUrl) {
        window.open(this.mediaUrl, '_blank');
      }
    },
    handleReply() {
      this.$emit('reply', this.message);
    },
  },
}
</script>

<style scoped>
.message-wrapper {
  display: flex;
  margin-bottom: 8px;
  padding: 0 8px;
}

.message-wrapper.sent {
  justify-content: flex-end;
}

.message-wrapper.received {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 12px;
  position: relative;
  word-wrap: break-word;
  animation: messageSlideIn 0.3s ease-out;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-bubble.sent {
  background: linear-gradient(135deg, #dcf8c6 0%, #c8e8b0 100%);
  border-bottom-right-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.message-bubble.received {
  background: #ffffff;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
}

.message-bubble.ai-message {
  border-left: 3px solid #25d366;
}

.ai-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #25d366;
  margin-bottom: 4px;
  padding: 2px 6px;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 8px;
}

.ai-indicator i {
  font-size: 0.75rem;
}

.message-sender {
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 4px;
  opacity: 0.8;
}

.message-bubble.sent .message-sender {
  color: #780f5a;
}

.message-bubble.received .message-sender {
  color: #780f5a;
}

.message-text {
  margin-bottom: 4px;
  white-space: pre-wrap;
}

.message-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}

.message-time {
  font-size: 0.7rem;
  color: #666;
}

.message-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.message-bubble:hover .message-actions {
  opacity: 1;
}

.action-btn {
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  color: #666;
  font-size: 0.85rem;
  border-radius: 4px;
  transition: background 0.2s;
}

.action-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}

.message-bubble.sent .action-btn {
  color: rgba(0, 0, 0, 0.6);
}

.message-bubble.received .action-btn {
  color: #666;
}

.message-details-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.details-content {
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.details-header h5 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #666;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.details-body {
  padding: 16px 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item.error {
  color: #dc3545;
}

.detail-label {
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
}

.detail-value {
  color: #333;
  font-size: 0.9rem;
  text-align: right;
}

.detail-value.small {
  font-size: 0.75rem;
  word-break: break-all;
}

.message-media {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: -10px -14px;
  margin-bottom: 4px;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
}

.message-media img {
  padding: 10px 30px;
}

.message-media img,
.message-media video {
  width: 100%;
  max-width: 400px;
  height: auto;
  max-height: 400px;
  object-fit: contain;
  border-radius: 4px;
  cursor: pointer;
  display: block;
}

.message-media video {
  max-height: 225px;
  padding: 10px 30px;
  border-radius: 4px;
}

.message-bubble.has-media {
  padding: 0;
}

.message-bubble.has-media .message-time {
  padding: 8px 14px;
  margin-top: 0;
}

.message-bubble.has-media .message-sender,
.message-bubble.has-media .ai-indicator {
  padding: 8px 14px 4px;
  margin-bottom: 0;
}

.message-audio {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-audio audio {
  max-width: 100%;
}

.message-document {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.message-document i {
  font-size: 1.2rem;
  color: #780f5a;
}

.download-link {
  margin-left: auto;
  color: #780f5a;
  text-decoration: none;
}

.message-bubble.deleted {
  opacity: 0.6;
  background: #f5f5f5 !important;
  border: 1px dashed #ccc !important;
}

.message-bubble.deleted.sent {
  background: #f0f0f0 !important;
}

.message-deleted {
  display: flex;
  align-items: center;
  gap: 6px;
  font-style: italic;
  color: #999;
  font-size: 0.9rem;
}

.message-deleted i {
  font-size: 0.85rem;
  opacity: 0.7;
}

.message-bubble.failed {
  border-left: 3px solid #dc3545;
}

.message-failed-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 4px;
  padding: 4px 8px;
  background-color: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
}

.message-failed-indicator i {
  font-size: 0.875rem;
}

.appointment-card {
  padding: 10px 12px;
  border-radius: 8px;
  background: #f5f5ff;
  border: 1px solid #d0d0ff;
  font-size: 0.85rem;
}

.appointment-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #343a40;
}

.appointment-row {
  display: flex;
  gap: 4px;
  margin-top: 2px;
}

.appointment-row .label {
  font-weight: 600;
}

.appointment-row .value {
  flex: 1;
  text-align: right;
}
</style>

